import { useMemo } from "react";
import { Button, Select, Table, theme } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { DefaultOptionType } from "antd/es/select";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { QtyInput } from "./QtyInput";
import { useItemsQuery } from "../helpers/queries";
import type { Item } from "../api-models";

export interface LineItem {
  key: string;
  // id of the persisted line this row came from, echoed back on save so the server can
  // keep server-only fields it never sends down for editing (e.g. a PR line's MRQ/
  // allocation trace). Absent on rows added in the editor.
  id?: number;
  itemId?: number;
  qty?: number;
  [extra: string]: unknown;
}

interface LineItemsEditorProps {
  items: LineItem[];
  onChange: (items: LineItem[]) => void;
  readOnly?: boolean;
  extraColumns?: ColumnsType<LineItem>;
  hint?: (itemId: number | undefined) => string | undefined;
  // Issuable qty per item at the store this document draws from. When given, the item
  // picker floats what is actually on the shelf to the top and greys out the rest —
  // omit it (rather than passing an empty map) when the balances aren't known, so
  // "nothing in stock" is never shown for "we didn't ask".
  availableByItem?: Map<number, number>;
}

// An option carries the balance alongside the plain-text label: search and the closed
// selector keep using `label`, while optionRender decorates the open list.
interface ItemOption extends DefaultOptionType {
  value: number;
  label: string;
  available?: number;
  unit?: string;
}

// Editable table used by every DocumentPage's line items while in DRAFT; read-only
// otherwise (architecture.md §11). ItemSelect shows code/unit; `hint` renders live
// stock/BOM-balance text per row.
export function LineItemsEditor({ items, onChange, readOnly, extraColumns, hint, availableByItem }: LineItemsEditorProps) {
  const { data: allItems = [] } = useItemsQuery();
  const { token } = theme.useToken();
  const byId = new Map<number, Item>(allItems.map((m) => [m.id, m]));

  const itemOptions = useMemo<DefaultOptionType[]>(() => {
    const toOption = (m: Item): ItemOption => ({
      value: m.id,
      label: `${m.code} — ${m.name}`,
      available: availableByItem?.get(m.id),
      unit: m.unit?.abbreviation,
    });
    if (!availableByItem) return allItems.map(toOption);
    const stocked: ItemOption[] = [];
    const rest: ItemOption[] = [];
    for (const m of allItems) ((availableByItem.get(m.id) ?? 0) > 0 ? stocked : rest).push(toOption(m));
    if (stocked.length === 0) return rest;
    return [
      { label: "In stock here", options: stocked },
      { label: "Not in stock here", options: rest },
    ];
  }, [allItems, availableByItem]);

  function renderOption(option: DefaultOptionType) {
    const data = option as ItemOption;
    const available = data.available ?? 0;
    const inStock = available > 0;
    return (
      <span style={{ display: "flex", justifyContent: "space-between", gap: 12, opacity: inStock ? 1 : 0.5 }}>
        <span>{data.label}</span>
        <span style={{ fontSize: 12, whiteSpace: "nowrap", color: inStock ? token.colorSuccess : token.colorTextTertiary }}>
          {inStock ? `${available.toFixed(3)} ${data.unit ?? ""}`.trim() : "no stock"}
        </span>
      </span>
    );
  }

  function updateRow(key: string, patch: Partial<LineItem>) {
    onChange(items.map((it) => (it.key === key ? { ...it, ...patch } : it)));
  }

  function addRow() {
    onChange([...items, { key: `new-${Date.now()}-${Math.random()}` }]);
  }

  function removeRow(key: string) {
    onChange(items.filter((it) => it.key !== key));
  }

  const columns: ColumnsType<LineItem> = [
    {
      // Its own column rather than folded into the item label: the code is what stores and
      // procurement actually work from, and it stays scannable down the page.
      title: "Code",
      key: "itemCode",
      width: 120,
      render: (_: unknown, row) => byId.get(row.itemId ?? -1)?.code ?? "-",
    },
    {
      title: "Item",
      dataIndex: "itemId",
      width: 260,
      render: (_: unknown, row) =>
        readOnly ? (
          byId.get(row.itemId ?? -1)?.name ?? "-"
        ) : (
          <Select
            showSearch
            value={row.itemId}
            placeholder="Select item"
            style={{ width: "100%" }}
            popupMatchSelectWidth={340}
            options={itemOptions}
            optionRender={availableByItem ? (option) => renderOption(option.data) : undefined}
            filterOption={(input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase())}
            onChange={(v) => updateRow(row.key, { itemId: v })}
          />
        ),
    },
    {
      title: "Qty",
      dataIndex: "qty",
      width: 160,
      render: (_: unknown, row) => {
        const unit = byId.get(row.itemId ?? -1)?.unit?.abbreviation;
        return readOnly ? (
          `${row.qty ?? 0} ${unit ?? ""}`
        ) : (
          <QtyInput value={row.qty} unit={unit} onChange={(v) => updateRow(row.key, { qty: v ?? undefined })} />
        );
      },
    },
    ...(hint
      ? [
          {
            title: "",
            key: "hint",
            render: (_: unknown, row: LineItem) => <span style={{ color: "#888", fontSize: 12 }}>{hint(row.itemId)}</span>,
          },
        ]
      : []),
    ...(extraColumns ?? []),
    ...(readOnly
      ? []
      : [
          {
            title: "",
            key: "remove",
            width: 48,
            render: (_: unknown, row: LineItem) => (
              <Button icon={<DeleteOutlined />} danger type="text" onClick={() => removeRow(row.key)} />
            ),
          },
        ]),
  ];

  return (
    <div data-tour="line-items">
      <Table<LineItem> rowKey="key" columns={columns} dataSource={items} pagination={false} size="small" scroll={{ x: "max-content" }} />
      {!readOnly && (
        <Button icon={<PlusOutlined />} onClick={addRow} style={{ marginTop: 8 }} data-tour="line-items-add">
          Add item
        </Button>
      )}
    </div>
  );
}
